/**
 * Win rate display helpers (plan 2149 C4). A win rate is won / (won + lost) and
 * ignores deals still open, so it is always shown with its closed n and open share.
 * null means nothing has closed: render an em dash, never 0.0%.
 */
export const EM_DASH = '—';

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

export function fmtWinRate(rate, digits = 1) {
  return isNum(rate) ? `${(rate * 100).toFixed(digits)}%` : EM_DASH;
}

export function fmtOpenShare(share) {
  if (!isNum(share)) return null;
  return `${Math.round(share * 100)}% still open`;
}

/** "n=4,283 closed · 52% still open"; parts are omitted when unknown. */
export function winRateContext({ n, openShare } = {}, sep = ' · ') {
  const parts = [];
  if (isNum(n)) parts.push(`n=${n.toLocaleString('en-US')} closed`);
  const open = fmtOpenShare(openShare);
  if (open) parts.push(open);
  return parts.join(sep);
}

/** "70.8% (n=4,283 closed, 52% still open)" for tooltips, exports and sentences. */
export function winRateInline(rate, ctx = {}) {
  const c = winRateContext(ctx, ', ');
  return c ? `${fmtWinRate(rate)} (${c})` : fmtWinRate(rate);
}

/** Pull the standard {win_rate_n, open_share} pair off an API row. */
export function ctxFromRow(row, prefix = '') {
  if (!row) return {};
  return { n: row[`${prefix}win_rate_n`], openShare: row[`${prefix}open_share`] };
}

/** Percentage-point gap, or null when either side is null. */
export function ppGap(a, b) {
  return isNum(a) && isNum(b) ? (a - b) * 100 : null;
}

/** Axis/compact money: $950K -> $950K, $1,000K -> $1M (no "$1000K"). */
export function fmtCompactMoney(v) {
  if (!isNum(v)) return EM_DASH;
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  const trim = (x) => String(Math.round(x * 10) / 10).replace(/\.0$/, '');
  if (abs >= 999_950_000) return `${sign}$${trim(abs / 1e9)}B`;
  if (abs >= 999_500) return `${sign}$${trim(abs / 1e6)}M`;
  if (abs >= 1000) return `${sign}$${Math.round(abs / 1000)}K`;
  return `${sign}$${Math.round(abs)}`;
}

export const MATURE_FOLLOWUP_DAYS = 180;
export const YOUNG_QUARTERS_NOTE = 'recent quarters still open: win rate shown only for quarters with at least 180 days of follow-up';

/**
 * Win-rate series for a quarterly trend: a quarter younger than 180 days since its end is mostly open and the few
 * quick wins that closed make the rate look like success, so its point is dropped. Labels read "2026-Q3" or "2026Q3".
 */
export function matureWinRates(quarters, rates, today = new Date()) {
  quarters = quarters || [];
  rates = rates || [];
  const young = quarters.map((label) => {
    const m = /(\d{4})\D*Q([1-4])/.exec(label || '');
    if (!m) return false;
    const end = Date.UTC(Number(m[1]), Number(m[2]) * 3, 0);
    return (today.getTime() - end) / 86400000 < MATURE_FOLLOWUP_DAYS;
  });
  return { rates: rates.map((r, i) => (young[i] ? null : r)), young, anyYoung: young.some(Boolean) };
}
